/**
 * React Query Background Refetch Strategies
 * Advanced strategies for keeping data fresh in the background
 */

import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { logger } from "@/lib/logger";

export interface RefetchStrategyOptions {
  // Stale time before considering refetch
  staleTime?: number;

  // Refetch interval for active queries
  refetchInterval?: number;

  // Refetch interval in background
  refetchIntervalInBackground?: boolean;

  // Refetch on window focus
  refetchOnWindowFocus?: boolean;

  // Refetch on reconnect
  refetchOnReconnect?: boolean;

  // Custom refetch condition
  shouldRefetch?: (data: any, queryKey: QueryKey) => boolean;

  // Maximum age before forced refetch
  maxAge?: number;

  // Priority-based refetch
  priority?: 'high' | 'medium' | 'low';
}

export interface SmartRefetchOptions extends RefetchStrategyOptions {
  // Query patterns to match
  patterns?: {
    exact?: QueryKey[];
    prefix?: string[];
    suffix?: string[];
    contains?: string[];
  };

  // Time-based strategies
  strategies?: {
    // Refetch during low-usage hours (2 AM - 6 AM)
    lowUsageHours?: boolean;

    // Refetch when tab becomes visible after being hidden
    onTabVisible?: boolean;

    // Refetch when network reconnects
    onNetworkReconnect?: boolean;

    // Refetch when device becomes idle
    onIdle?: boolean;

    // Refetch before cache expires
    beforeExpire?: boolean;
  };

  // Batch refetch settings
  batch?: {
    enabled?: boolean;
    size?: number;
    delay?: number;
  };
}

/**
 * Background refetch scheduler
 */
export class BackgroundRefetchScheduler {
  private queryClient: QueryClient;
  private options: SmartRefetchOptions;
  private intervals: Map<string, NodeJS.Timeout> = new Map();
  private visibilityListener: (() => void) | null = null;
  private networkListener: (() => void) | null = null;
  private idleCallback: number | null = null;

  constructor(queryClient: QueryClient, options: SmartRefetchOptions = {}) {
    this.queryClient = queryClient;
    this.options = {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      priority: 'medium',
      patterns: {},
      strategies: {
        lowUsageHours: false,
        onTabVisible: true,
        onNetworkReconnect: true,
        onIdle: false,
        beforeExpire: true,
      },
      batch: {
        enabled: true,
        size: 10,
        delay: 100,
      },
      ...options,
    };
  }

  /**
   * Start background refetching
   */
  start(): void {
    this.setupEventListeners();
    this.scheduleRefetches();

    logger.info('🔄 Background refetch scheduler started', {
      eventType: 'REFETCH_SCHEDULER_STARTED',
      options: this.options,
    });
  }

  /**
   * Stop background refetching
   */
  stop(): void {
    // Clear all intervals
    this.intervals.forEach((interval) => clearInterval(interval));
    this.intervals.clear();

    // Remove event listeners
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }

    if (this.networkListener) {
      window.removeEventListener('online', this.networkListener);
      this.networkListener = null;
    }

    if (this.idleCallback) {
      cancelIdleCallback(this.idleCallback);
      this.idleCallback = null;
    }

    logger.info('⏹️ Background refetch scheduler stopped', {
      eventType: 'REFETCH_SCHEDULER_STOPPED',
    });
  }

  /**
   * Setup event listeners for smart refetching
   */
  private setupEventListeners(): void {
    const { strategies } = this.options;

    // Tab visibility change
    if (strategies?.onTabVisible) {
      this.visibilityListener = () => {
        if (document.visibilityState === 'visible') {
          this.refetchOnTabVisible();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityListener);
    }

    // Network reconnection
    if (strategies?.onNetworkReconnect) {
      this.networkListener = () => {
        this.refetchOnNetworkReconnect();
      };
      window.addEventListener('online', this.networkListener);
    }

    // Device idle
    if (strategies?.onIdle && 'requestIdleCallback' in window) {
      this.scheduleIdleRefetch();
    }
  }

  /**
   * Schedule refetches based on strategies
   */
  private scheduleRefetches(): void {
    const { strategies, refetchInterval } = this.options;

    // Schedule interval-based refetch
    if (refetchInterval) {
      const intervalId = setInterval(() => {
        this.refetchStaleQueries();
      }, refetchInterval);
      this.intervals.set('interval', intervalId);
    }

    // Schedule low-usage hours refetch
    if (strategies?.lowUsageHours) {
      this.scheduleLowUsageHoursRefetch();
    }

    // Schedule pre-expire refetch
    if (strategies?.beforeExpire && this.options.maxAge) {
      this.schedulePreExpireRefetch();
    }
  }

  /**
   * Refetch queries when tab becomes visible
   */
  private refetchOnTabVisible(): void {
    logger.info('👁️ Tab became visible, refetching stale queries', {
      eventType: 'TAB_VISIBLE_REFETCH',
    });

    const queries = this.getMatchingQueries();
    const staleQueries = queries.filter((query) => {
      const data = query.state.data;
      const queryKey = query.queryKey;
      return this.options.shouldRefetch?.(data, queryKey) !== false &&
             this.isQueryStale(query);
    });

    this.refetchQueries(staleQueries);
  }

  /**
   * Refetch queries when network reconnects
   */
  private refetchOnNetworkReconnect(): void {
    logger.info('🌐 Network reconnected, refetching all matching queries', {
      eventType: 'NETWORK_RECONNECT_REFETCH',
    });

    const queries = this.getMatchingQueries();
    this.refetchQueries(queries);
  }

  /**
   * Schedule refetch during low-usage hours (2 AM - 6 AM)
   */
  private scheduleLowUsageHoursRefetch(): void {
    const now = new Date();
    const currentHour = now.getHours();

    // Calculate next low-usage window
    let nextRefetch: Date;
    if (currentHour >= 2 && currentHour < 6) {
      // Currently in low-usage hours, schedule for tomorrow
      nextRefetch = new Date(now);
      nextRefetch.setDate(nextRefetch.getDate() + 1);
      nextRefetch.setHours(2, 0, 0, 0);
    } else if (currentHour >= 6) {
      // After low-usage hours, schedule for tomorrow
      nextRefetch = new Date(now);
      nextRefetch.setDate(nextRefetch.getDate() + 1);
      nextRefetch.setHours(2, 0, 0, 0);
    } else {
      // Before low-usage hours, schedule for today
      nextRefetch = new Date(now);
      nextRefetch.setHours(2, 0, 0, 0);
    }

    const delay = nextRefetch.getTime() - now.getTime();

    setTimeout(() => {
      logger.info('🌙 Low-usage hours refetch', {
        eventType: 'LOW_USAGE_REFETCH',
        time: new Date().toISOString(),
      });

      const queries = this.getMatchingQueries();
      this.refetchQueries(queries);

      // Schedule next
      this.scheduleLowUsageHoursRefetch();
    }, delay);
  }

  /**
   * Schedule refetch before cache expires
   */
  private schedulePreExpireRefetch(): void {
    const { maxAge } = this.options;
    if (!maxAge) return;

    // Refetch 1 minute before expiration
    const preExpireDelay = maxAge - 60000;

    if (preExpireDelay > 0) {
      setTimeout(() => {
        logger.info('⏰ Pre-expire refetch', {
          eventType: 'PRE_EXPIRE_REFETCH',
          maxAgeMs: maxAge,
        });

        const queries = this.getMatchingQueries();
        this.refetchQueries(queries);
      }, preExpireDelay);
    }
  }

  /**
   * Schedule idle-time refetch
   * DISABLED: Was causing excessive logging spam
   * TODO: Re-enable with proper throttling if needed
   */
  private scheduleIdleRefetch(): void {
    // Disabled to prevent log spam - idle refetch is not critical
    return;
  }

  /**
   * Get queries matching the configured patterns
   */
  private getMatchingQueries() {
    const { patterns } = this.options;
    const cache = this.queryClient.getQueryCache();
    const allQueries = cache.getAll();

    if (!patterns || Object.keys(patterns).length === 0) {
      return allQueries;
    }

    return allQueries.filter((query) => {
      const queryKeyStr = JSON.stringify(query.queryKey);

      // Check exact matches
      if (patterns.exact?.some((key) => JSON.stringify(key) === queryKeyStr)) {
        return true;
      }

      // Check prefix matches
      if (patterns.prefix?.some((prefix) => queryKeyStr.startsWith(prefix))) {
        return true;
      }

      // Check suffix matches
      if (patterns.suffix?.some((suffix) => queryKeyStr.endsWith(suffix))) {
        return true;
      }

      // Check contains matches
      if (patterns.contains?.some((contains) => queryKeyStr.includes(contains))) {
        return true;
      }

      return false;
    });
  }

  /**
   * Check if query is stale
   */
  private isQueryStale(query: any): boolean {
    const { staleTime } = this.options;
    if (!staleTime) return false;

    const dataUpdatedAt = query.state.dataUpdatedAt;
    const now = Date.now();
    const timeSinceUpdate = now - dataUpdatedAt;

    return timeSinceUpdate > staleTime;
  }

  /**
   * Refetch queries with batching support
   */
  private async refetchQueries(queries: any[]): Promise<void> {
    const { batch: batchOptions } = this.options;

    if (!batchOptions?.enabled || queries.length <= batchOptions.size!) {
      // Refetch all at once
      await Promise.all(
        queries.map((query) =>
          this.queryClient.refetchQueries({ queryKey: query.queryKey })
        )
      );
    } else {
      // Batch refetch
      const batches = this.chunkArray(queries, batchOptions.size!);

      for (const queryBatch of batches) {
        await Promise.all(
          queryBatch.map((query) =>
            this.queryClient.refetchQueries({ queryKey: query.queryKey })
          )
        );

        // Delay between batches
        await this.delay(batchOptions.delay ?? 0);
      }
    }
  }

  /**
   * Refetch only stale queries
   */
  private refetchStaleQueries(): void {
    const staleQueries = this.getMatchingQueries().filter((query) =>
      this.isQueryStale(query)
    );

    if (staleQueries.length > 0) {
      logger.info('🔄 Refetching stale queries', {
        eventType: 'STALE_REFETCH',
        queryCount: staleQueries.length,
      });

      this.refetchQueries(staleQueries);
    }
  }

  /**
   * Utility function to chunk array
   */
  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  /**
   * Utility function to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/**
 * Create background refetch scheduler with default settings
 */
export function createBackgroundRefetchScheduler(
  queryClient: QueryClient,
  options?: SmartRefetchOptions
): BackgroundRefetchScheduler {
  return new BackgroundRefetchScheduler(queryClient, options);
}

/**
 * Hook to use background refetch scheduler
 */
export function useBackgroundRefetchScheduler(
  queryClient: QueryClient,
  options?: SmartRefetchOptions
) {
  const schedulerRef = useRef<BackgroundRefetchScheduler | null>(null);

  useEffect(() => {
    if (!schedulerRef.current) {
      schedulerRef.current = createBackgroundRefetchScheduler(queryClient, options);
      schedulerRef.current.start();
    }

    return () => {
      schedulerRef.current?.stop();
    };
  }, [queryClient, options]);

  return schedulerRef.current;
}

// Import React hooks
import { useEffect, useRef } from "react";